import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { convertSpecialPanelCheckedItemsToArray } from '@functional';

import Box from '@mui/material/Box';

import ConditionBox from '@ui-parts/ConditionBox';
import SpecialPanelModal from '@ui-parts/SpecialPanelModal';

import { SPECIAL_PANEL_DISPLAY_SETTING_IDS, SPECIAL_PANEL_CATEGORIES } from '~/constants';

const FlexBox = styled(Box)`
  display: flex;
  align-items: flex-start;
`;

const FlexItem = styled(Box)`
  width: 100%;
  flex-grow: 1;
  margin: 10px 0 0;
  padding: 0 5px;
`;

function DetailedConditionsAreaSpecialPanelTwo({ checkedItems }) {
  const { t } = useTranslation();

  const SPECIAL_PANEL_DISPLAY_SETTING_ID = SPECIAL_PANEL_DISPLAY_SETTING_IDS.V2;

  const [openModals, setOpenModals] = useState({
    jobType: false,
    jobGroup: false,
    industry: false,
    numOfWorkers: false,
    jobPosition: false,
    householdIncome: false,
    marriageStatus: false,
    childrenStatus: false,
  });

  const [selectedChoiceNames, setSelectedChoiceNames] = useState({
    jobType: [],
    jobGroup: [],
    industry: [],
    numOfWorkers: [],
    jobPosition: [],
    householdIncome: [],
    marriageStatus: [],
    childrenStatus: [],
  });

  const selectedJobType = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.jobType,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.jobType]);

  const selectedJobGroup = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.jobGroup,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.jobGroup]);

  const selectedIndustry = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.industry,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.industry]);

  const selectedNumOfWorkers = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.numOfWorkers,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.numOfWorkers]);

  const selectedJobPosition = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.jobPosition,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.jobPosition]);

  const selectedHouseholdIncome = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.householdIncome,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.householdIncome]);

  const selectedMarriageStatus = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.marriageStatus,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.marriageStatus]);

  const selectedChildrenStatus = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      checkedItems.childrenStatus,
      SPECIAL_PANEL_DISPLAY_SETTING_ID,
      true
    );
  }, [checkedItems.childrenStatus]);

  useEffect(() => {
    setSelectedChoiceNames({
      jobType: selectedJobType,
      jobGroup: selectedJobGroup,
      industry: selectedIndustry,
      numOfWorkers: selectedNumOfWorkers,
      jobPosition: selectedJobPosition,
      householdIncome: selectedHouseholdIncome,
      marriageStatus: selectedMarriageStatus,
      childrenStatus: selectedChildrenStatus,
    });
  }, [
    selectedJobType,
    selectedJobGroup,
    selectedIndustry,
    selectedNumOfWorkers,
    selectedJobPosition,
    selectedHouseholdIncome,
    selectedMarriageStatus,
    selectedChildrenStatus,
  ]);

  return (
    <>
      <FlexBox>
        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.jobGroup`)}
            selectedChoices={selectedChoiceNames.jobGroup}
            name="QuotaJobGroup"
            editable={selectedChoiceNames.jobGroup.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                jobGroup: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                jobGroup: true,
              });
            }}
          />
        </FlexItem>

        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.jobType`)}
            selectedChoices={selectedChoiceNames.jobType}
            name="QuotaJobType"
            editable={selectedChoiceNames.jobType.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                jobType: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                jobType: true,
              });
            }}
          />
        </FlexItem>
      </FlexBox>

      <FlexBox>
        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.industry`)}
            selectedChoices={selectedChoiceNames.industry}
            name="QuotaIndustry"
            editable={selectedChoiceNames.industry.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                industry: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                industry: true,
              });
            }}
          />
        </FlexItem>

        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.numOfWorkers`)}
            selectedChoices={selectedChoiceNames.numOfWorkers}
            name="QuotaNumOfWorkers"
            editable={selectedChoiceNames.numOfWorkers.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                numOfWorkers: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                numOfWorkers: true,
              });
            }}
          />
        </FlexItem>
      </FlexBox>

      <FlexBox>
        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.jobPosition`)}
            selectedChoices={selectedChoiceNames.jobPosition}
            name="QuotaJobPosition"
            editable={selectedChoiceNames.jobPosition.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                jobPosition: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                jobPosition: true,
              });
            }}
          />
        </FlexItem>

        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.householdIncome`)}
            selectedChoices={selectedChoiceNames.householdIncome}
            name="QuotaHouseholdIncome"
            editable={selectedChoiceNames.householdIncome.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                householdIncome: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                householdIncome: true,
              });
            }}
          />
        </FlexItem>
      </FlexBox>

      <FlexBox>
        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.marriageStatus`)}
            selectedChoices={selectedChoiceNames.marriageStatus}
            name="QuotaMarriageStatus"
            editable={selectedChoiceNames.marriageStatus.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                marriageStatus: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                marriageStatus: true,
              });
            }}
          />
        </FlexItem>

        <FlexItem>
          <ConditionBox
            conditionLabel={t(`quotas.conditions.titles.childrenStatus`)}
            selectedChoices={selectedChoiceNames.childrenStatus}
            name="QuotaChildrenStatus"
            editable={selectedChoiceNames.childrenStatus.length > 0}
            handleClickAddBtn={() => {
              setOpenModals({
                ...openModals,
                childrenStatus: true,
              });
            }}
            handleClickEditBtn={() => {
              setOpenModals({
                ...openModals,
                childrenStatus: true,
              });
            }}
          />
        </FlexItem>
      </FlexBox>

      {SPECIAL_PANEL_CATEGORIES[SPECIAL_PANEL_DISPLAY_SETTING_ID].map((questionKey) => (
        <SpecialPanelModal
          key={questionKey}
          open={openModals[questionKey]}
          handleClose={() => {
            setOpenModals({
              ...openModals,
              [questionKey]: false,
            });
          }}
          specialPanelDisplaySettingId={SPECIAL_PANEL_DISPLAY_SETTING_ID}
          questionKey={questionKey}
          checkedChoices={checkedItems[questionKey]}
        />
      ))}
    </>
  );
}

DetailedConditionsAreaSpecialPanelTwo.propTypes = {
  // ↓i動的なのでいったんlintエラーは無視してobjectで定義
  // eslint-disable-next-line react/forbid-prop-types
  checkedItems: PropTypes.object.isRequired,
};

export default DetailedConditionsAreaSpecialPanelTwo;
